import React, { useState, useEffect } from 'react';
import { X, Plus } from 'lucide-react';
import { createPortal } from 'react-dom';
import { getPersonInitials } from '../utils/personInitials';

interface Resource {
  id: string;
  first_name: string;
  last_name: string;
  avatar_url?: string | null;
}

interface MoveBookingConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (updatedData: {
    dropOffDate?: string;
    dropOffTime?: string;
    startDate: string;
    startTime: string;
    endDate: string;
    endTime: string;
    resources: Resource[];
  }) => void;
  bookingTitle: string;
  dropOffDate?: string;
  dropOffTime?: string;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  resources?: Resource[];
  availableResources?: Resource[];
}

export default function MoveBookingConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  bookingTitle,
  dropOffDate,
  dropOffTime,
  startDate,
  startTime,
  endDate,
  endTime,
  resources = [],
  availableResources = []
}: MoveBookingConfirmModalProps) {
  const [editedDropOffDate, setEditedDropOffDate] = useState(dropOffDate || '');
  const [editedDropOffTime, setEditedDropOffTime] = useState(dropOffTime || '');
  const [editedStartDate, setEditedStartDate] = useState(startDate);
  const [editedStartTime, setEditedStartTime] = useState(startTime);
  const [editedEndDate, setEditedEndDate] = useState(endDate);
  const [editedEndTime, setEditedEndTime] = useState(endTime);
  const [selectedResources, setSelectedResources] = useState<Resource[]>(resources);
  const [showResourcePicker, setShowResourcePicker] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  // Update state when props change
  useEffect(() => {
    console.log('🔍 MoveBookingConfirmModal - Props received:', {
      resources,
      availableResources,
      resourcesLength: resources.length,
      availableResourcesLength: availableResources.length
    });
    setEditedDropOffDate(dropOffDate || '');
    setEditedDropOffTime(dropOffTime || '');
    setEditedStartDate(startDate);
    setEditedStartTime(startTime);
    setEditedEndDate(endDate);
    setEditedEndTime(endTime);
    setSelectedResources(resources);
  }, [dropOffDate, dropOffTime, startDate, startTime, endDate, endTime, resources, availableResources]);

  // Handle animation when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    } else {
      const timer = setTimeout(() => setIsAnimating(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!isOpen && !isAnimating) return null;

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const handleConfirm = () => {
    onConfirm({
      dropOffDate: editedDropOffDate || undefined,
      dropOffTime: editedDropOffTime || undefined,
      startDate: editedStartDate,
      startTime: editedStartTime,
      endDate: editedEndDate,
      endTime: editedEndTime,
      resources: selectedResources
    });
  };

  const handleRemoveResource = (resourceId: string) => {
    setSelectedResources(prev => prev.filter(r => r.id !== resourceId));
  };

  const handleAddResource = (resource: Resource) => {
    if (!selectedResources.find(r => r.id === resource.id)) {
      setSelectedResources(prev => [...prev, resource]);
    }
    setShowResourcePicker(false);
  };

  const unassignedResources = availableResources.filter(
    r => !selectedResources.find(sr => sr.id === r.id)
  );

  console.log('🔍 MoveBookingConfirmModal - Computed values:', {
    selectedResourcesCount: selectedResources.length,
    unassignedResourcesCount: unassignedResources.length,
    showResourcePicker,
    selectedResources: selectedResources.map(r => ({ id: r.id, name: `${r.first_name} ${r.last_name}` })),
    unassignedResources: unassignedResources.map(r => ({ id: r.id, name: `${r.first_name} ${r.last_name}` }))
  });

  const getResourceAvatar = (resource: Resource) => {
    const initials = getPersonInitials(resource.first_name, resource.last_name);
    
    if (resource.avatar_url) {
      return (
        <img
          src={resource.avatar_url}
          alt={`${resource.first_name} ${resource.last_name}`}
          className="w-full h-full object-cover"
        />
      );
    }
    return <span className="text-xs font-bold text-white">{initials}</span>;
  };

  const modalContent = (
    <div 
      className={`fixed inset-0 z-[999999] flex items-center justify-center p-4 transition-all duration-300 ${
        isOpen ? 'bg-black/40 backdrop-blur-sm' : 'bg-black/0'
      }`}
      onClick={handleClose}
      style={{
        pointerEvents: isAnimating ? 'auto' : 'none'
      }}
    >
      {/* Modal */}
      <div 
        className={`relative bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden transition-all duration-300 ease-out ${
          isOpen 
            ? 'opacity-100 scale-100 translate-y-0' 
            : 'opacity-0 scale-95 translate-y-4'
        }`}
        onClick={e => e.stopPropagation()}
        style={{
          transitionTimingFunction: isOpen ? 'cubic-bezier(0.4, 0, 0.2, 1)' : 'cubic-bezier(0.4, 0, 1, 1)'
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">
            {bookingTitle}
          </h2>
          <button
            onClick={handleClose}
            className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Drop Off Date & Time */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Drop Off (Optional)
            </label>
            <div className="grid grid-cols-2 gap-2">
              <input
                type="date"
                value={editedDropOffDate}
                onChange={(e) => setEditedDropOffDate(e.target.value)}
                className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
              <input
                type="time"
                value={editedDropOffTime}
                onChange={(e) => setEditedDropOffTime(e.target.value)}
                className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
            </div>
          </div>

          {/* Start Date & Time */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Start
            </label>
            <div className="grid grid-cols-2 gap-2">
              <input
                type="date"
                value={editedStartDate}
                onChange={(e) => setEditedStartDate(e.target.value)}
                className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
              <input
                type="time"
                value={editedStartTime}
                onChange={(e) => setEditedStartTime(e.target.value)}
                className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
            </div>
          </div>

          {/* End Date & Time */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              End
            </label>
            <div className="grid grid-cols-2 gap-2">
              <input
                type="date"
                value={editedEndDate}
                onChange={(e) => setEditedEndDate(e.target.value)}
                className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
              <input
                type="time"
                value={editedEndTime}
                onChange={(e) => setEditedEndTime(e.target.value)}
                className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
            </div>
          </div>

          {/* Assigned Resources - BookingInfoModal Style */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Team Members
              </label>
              {unassignedResources.length > 0 && selectedResources.length > 0 && (
                <button
                  onClick={() => setShowResourcePicker(!showResourcePicker)}
                  className="text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors"
                >
                  {showResourcePicker ? 'Cancel' : '+ Add'}
                </button>
              )}
            </div>

            {/* Resource Picker Dropdown */}
            {showResourcePicker && unassignedResources.length > 0 && (
              <div className="mb-3 bg-gray-50 border border-gray-200 rounded-xl p-2 max-h-40 overflow-y-auto">
                {unassignedResources.map(resource => (
                  <button
                    key={resource.id}
                    onClick={() => handleAddResource(resource)}
                    className="w-full flex items-center space-x-3 p-2 hover:bg-white rounded-lg transition-colors"
                  >
                    <div className="h-8 w-8 rounded-full bg-gradient-to-br from-gray-400 to-gray-600 flex items-center justify-center overflow-hidden flex-shrink-0">
                      {getResourceAvatar(resource)}
                    </div>
                    <span className="text-sm font-medium text-gray-900 text-left">
                      {resource.first_name} {resource.last_name}
                    </span>
                  </button>
                ))}
              </div>
            )}

            {/* Selected Resources List */}
            <div className="bg-gray-50 border border-gray-200 rounded-xl overflow-hidden">
              {selectedResources.length === 0 ? (
                unassignedResources.length > 0 ? (
                  <button
                    onClick={() => {
                      console.log('🎯 Clicked empty state to add resources');
                      setShowResourcePicker(true);
                    }}
                    className="w-full py-4 text-center hover:bg-gray-100 transition-colors"
                  >
                    <p className="text-xs text-gray-500 italic">No resources assigned</p>
                    <p className="text-xs text-blue-600 font-medium mt-1">Tap to add</p>
                  </button>
                ) : (
                  <div className="py-4 text-center">
                    <p className="text-xs text-gray-500 italic">No resources available</p>
                  </div>
                )
              ) : (
                <div className="divide-y divide-gray-200">
                  {selectedResources.map((resource) => (
                    <div key={resource.id} className="flex items-center justify-between p-3">
                      <div className="flex items-center space-x-3">
                        <div className="h-8 w-8 rounded-full bg-gradient-to-br from-gray-400 to-gray-600 flex items-center justify-center overflow-hidden flex-shrink-0">
                          {getResourceAvatar(resource)}
                        </div>
                        <div className="text-sm font-medium text-gray-900">
                          {resource.first_name} {resource.last_name}
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveResource(resource.id)}
                        className="text-xs font-medium text-red-600 hover:text-red-700 transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer - iOS style */}
        <div className="px-5 py-4 bg-gray-50/50 border-t border-gray-100">
          <button
            onClick={handleConfirm}
            className="w-full px-4 py-3 text-white bg-blue-600 rounded-xl hover:bg-blue-700 active:scale-[0.98] transition-all font-semibold text-sm shadow-sm touch-manipulation"
            style={{ 
              WebkitTapHighlightColor: 'transparent',
              touchAction: 'manipulation'
            }}
          >
            Move Booking
          </button>
          <p className="text-xs text-gray-500 text-center mt-3">
            Drag and drop to change the date after saving
          </p>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
